import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, TrendingDown, CalendarClock, Layers, Crosshair, Tag } from "lucide-react";
import CheckinComparison from "@/components/session/CheckinComparison";
import { useDemoState } from "../demo/DemoStateProvider";
import { SettingsSnapshot } from "../demo/SettingsSnapshot";

/**
 * DemoSessionDetail (/client/:id/session/:sessionId) — one session in full, showing
 * the real hierarchy: Client → Session → Theme → Specific Topic → Target Emotion →
 * Check-ins → Settings snapshot. Pre/post use the real CheckinComparison leaf.
 */
export function DemoSessionDetail() {
  const { id = "", sessionId = "" } = useParams();
  const { clientById, sessionsFor } = useDemoState();
  const client = clientById(id);
  const session = sessionsFor(id).find((s) => s.id === sessionId);

  if (!client || !session) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-16 text-center">
        <p className="font-heading text-2xl text-[var(--color-text-primary)]">Session not found</p>
        <Link to="/dashboard" className="mt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wide text-[var(--color-gold-soft)]">
          <ArrowLeft className="h-3 w-3" /> Back to dashboard
        </Link>
      </main>
    );
  }

  // Only a real pre AND post reading yields a distress-change badge (null = skipped scale).
  const delta = session.pre.distress != null && session.post.distress != null ? session.pre.distress - session.post.distress : 0;

  return (
    <main className="mx-auto max-w-4xl px-4 py-8">
      <Link to={`/client/${client.id}`} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)] transition-colors hover:text-[var(--color-gold-soft)]">
        <ArrowLeft className="h-3 w-3" /> {client.displayName}
      </Link>

      {/* Hierarchy header: Client → Session → Theme → Topic → Target emotion */}
      <header className="mt-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">
          {client.displayName} · {session.date} · {session.durationMin} min · {session.modality}
        </p>
        <h1 className="mt-1 font-heading text-3xl font-medium text-[var(--color-text-primary)]">{session.topic}</h1>
        <div className="mt-3 flex flex-wrap gap-2">
          <Chip icon={<Layers className="h-3.5 w-3.5" />} label="Theme" value={session.theme} />
          <Chip icon={<Tag className="h-3.5 w-3.5" />} label="Topic" value={session.topic} />
          <Chip icon={<Crosshair className="h-3.5 w-3.5" />} label="Target emotion" value={session.targetEmotion} accent />
          {delta > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border-gold)] px-3 py-1 font-mono text-[10px] uppercase tracking-wide text-[var(--color-gold-soft)]">
              <TrendingDown className="h-3.5 w-3.5" /> Distress {session.pre.distress} → {session.post.distress} (−{delta})
            </span>
          )}
        </div>
      </header>

      {/* Pre → Post check-in (real CheckinComparison leaf) */}
      <section className="mt-6 grid gap-5 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
        <CheckinComparison title="At the start" emotions={session.pre.emotions} distress={session.pre.distress} capability={session.pre.capability} goal={session.pre.goal} />
        <div className="hidden items-center justify-center pt-10 text-[var(--color-gold-soft)] sm:flex"><ArrowRight className="h-5 w-5" /></div>
        <CheckinComparison title="At the end" emotions={session.post.emotions} distress={session.post.distress} capability={session.post.capability} note={session.post.note} />
      </section>

      {/* Settings snapshot (shared component) */}
      <section className="mt-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-5">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--color-text-muted)]">Session settings snapshot</h2>
        <p className="mt-0.5 text-[12px] text-[var(--color-text-ghost)]">Exactly how this session was run.</p>
        <SettingsSnapshot settings={session.settings} />
      </section>

      {/* Follow-up */}
      {session.followUp && (
        <section className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-5 py-4">
          <CalendarClock className="h-4 w-4 text-[var(--color-gold-soft)]" />
          <span className="font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">Follow-up due {session.followUp.dueDate}</span>
          {session.followUp.note && <span className="text-[13px] text-[var(--color-text-ghost)]">— {session.followUp.note}</span>}
        </section>
      )}
    </main>
  );
}

function Chip({ icon, label, value, accent }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-wide ${accent ? "border-[var(--color-border-gold)] text-[var(--color-gold-soft)]" : "border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
      {icon} <span className="text-[var(--color-text-ghost)]">{label}:</span> {value}
    </span>
  );
}
